import React from 'react'
import { AnimatePresence, motion } from "motion/react"
import { SidebarContext } from './sidebar-context'
import type { DivProps, ButtonProps } from './sidebar-types'

export function SidebarProvider({children}:{children:React.ReactNode}){
  const [collapsed,setCollapsed] = React.useState(()=>{
    if (typeof window !== 'undefined') {
      return window.matchMedia('(max-width: 767px)').matches
    }
    return false
  })
  const toggle = ()=>setCollapsed(c=>!c)
  return <SidebarContext.Provider value={{collapsed,toggle}}>{children}</SidebarContext.Provider>
}

export function Sidebar({children,className=''}: DivProps){
  const {collapsed, toggle} = React.useContext(SidebarContext)
  const base = 'flex flex-col h-screen shrink-0 border-r border-slate-200 bg-white overflow-hidden'
  const [isMobile, setIsMobile] = React.useState(() => {
    if (typeof window === "undefined") return false
    return window.matchMedia("(max-width: 767px)").matches
  })
  React.useEffect(() => {
    if (typeof window === "undefined") return
    const mq = window.matchMedia("(max-width: 767px)")
    const onChange = () => setIsMobile(mq.matches)
    onChange()
    mq.addEventListener?.("change", onChange)
    return () => mq.removeEventListener?.("change", onChange)
  }, [])

  const expandedW = 256
  const collapsedW = 64
  const openOnMobile = isMobile && !collapsed
  const asidePosition = isMobile ? "fixed inset-y-0 left-0 z-40" : "relative"
  const interaction = isMobile && collapsed ? "pointer-events-none" : ""
  const transition = { duration: 0.28, ease: [0.4, 0, 0.2, 1] as [number, number, number, number] } as const

  return (
    <>
      <AnimatePresence>
        {openOnMobile && (
          <motion.div
            key="sidebar-overlay"
            className="fixed inset-0 z-30 bg-black/20 md:hidden"
            aria-hidden="true"
            onClick={toggle}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={transition}
          />
        )}
      </AnimatePresence>
      <motion.aside
        className={`${className || ""} ${base} ${asidePosition} ${interaction} md:flex`}
        aria-label="Hauptnavigation"
        aria-hidden={isMobile && collapsed ? true : undefined}
        data-collapsed={collapsed}
        initial={false}
        animate={
          isMobile
            ? { x: collapsed ? -expandedW : 0, width: expandedW }
            : { width: collapsed ? collapsedW : expandedW, x: 0 }
        }
        transition={transition}
        style={{ willChange: "width, transform" }}
      >
        {/* Mobile Close Button */}
        <button
          aria-label="Sidebar schließen"
          title="Schließen"
          onClick={toggle}
          className="md:hidden absolute top-2 right-2 inline-flex items-center justify-center h-9 w-9 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 shadow-sm"
        >
          <span className="sr-only">Schließen</span>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
        </button>
        {children}
      </motion.aside>
    </>
  )
}

export function SidebarHeader({children,className=''}: DivProps){
  // Put default classes first, so callers can override (e.g. padding in collapsed mode).
  return <div className={'p-4 border-b border-slate-200 ' + (className||'')}>{children}</div>
}
export function SidebarContent({children,className=''}: DivProps){
  // Reserve scrollbar gutter so icons (e.g. Profil/Wissenswertes) don't "jump" when the scrollbar appears/disappears.
  return (
    <div
      className={(className||'') + ' flex-1 overflow-y-auto overflow-x-hidden p-3'}
      style={{ scrollbarGutter: "stable" }}
    >
      {children}
    </div>
  )
}
export function SidebarGroup({children,className=''}: DivProps){ return <div className={(className||'') + ' py-2'}>{children}</div> }
export function SidebarGroupLabel({children,className=''}: DivProps){ return <div className={(className||'') + ' px-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500'}>{children}</div> }
export function SidebarGroupContent({children,className=''}: DivProps){ return <div className={(className||'') + ' mt-2'}>{children}</div> }
// grid-cols-[minmax(0,1fr)]: verhindert, dass breite Untermenü-Einträge die implizite
// auto-Spalte (min-content) aufweiten und alle Zeilen/Chevrons nach rechts schieben.
export function SidebarMenu({children,className=''}: DivProps){ return <nav className={(className||'') + ' grid grid-cols-[minmax(0,1fr)] gap-1'}>{children}</nav> }
export function SidebarMenuItem({children,className=''}: DivProps){ return <div className={(className||'') + ' min-w-0'}>{children}</div> }
export function SidebarMenuButton({children,className='',onClick,...rest}: ButtonProps){ return <button className={(className||'') + ' w-full text-left px-3 py-2 rounded-lg border transition'} onClick={onClick} {...rest}>{children}</button> }

export function SidebarInset({children,className=''}: DivProps){ return <div className={(className||'') + ' flex-1 min-w-0 h-screen flex flex-col'}>{children}</div> }
export function SidebarTrigger({className='',...rest}: ButtonProps){
  const {toggle}=React.useContext(SidebarContext)
  return (
    <button
      aria-label="Sidebar umschalten"
      className={(className||'') + ' inline-flex items-center justify-center h-9 w-9 shrink-0 flex-none rounded-lg border border-slate-200 bg-white hover:bg-slate-50'}
      onClick={toggle}
      {...rest}
    >
      <span className="sr-only">Menu</span>
      <svg className="h-[18px] w-[18px] shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 6h18M3 12h18M3 18h18" />
      </svg>
    </button>
  )
}
